import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@shared/hooks/useAuth';
import { CatLogo } from '@shared/components/CatLogo';
import { ChevronDown, Menu, X, LogOut, Settings, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSwitcher from '@/components/LanguageSwitcher';

/* ── Apple-style spring ── */
const spring = { type: 'spring', bounce: 0, duration: 0.4 };
const springBounce = { type: 'spring', bounce: 0.15, duration: 0.5 };

/* ── Dropdown ── */
const NavDropdown = ({ group, checkActive, onNav }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}>
      <button className={`nav-link ${checkActive(group.items.map(i => i.href)) ? 'nav-link-active' : ''}`}>
        {group.label}
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={spring}>
          <ChevronDown className="w-3 h-3 opacity-40" />
        </motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <div className="dropdown-positioner">
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={springBounce}
              className="dropdown-glass">
              {group.items.map((item) => (
                <button key={item.href}
                  onClick={() => { setOpen(false); onNav(item.href); }}
                  className={`dropdown-item ${checkActive([item.href]) ? 'dropdown-item-active' : ''}`}>
                  <span>{item.label}</span>
                </button>
              ))}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Mobile accordion ── */
const MobileGroup = ({ group, onNav }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="mobile-group-header">
        <span>{group.label}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={spring}>
          <ChevronDown className="w-3.5 h-3.5 opacity-30" />
        </motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={spring}
            className="mobile-group-items">
            {group.items.map((item) => (
              <button key={item.href} onClick={() => onNav(item.href)} className="mobile-item">
                {item.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ════════════════════════════════════════════
   Navbar — Apple / LoveFrom Design Language
   视觉重构：排版 · 间距 · 层级 · Motion
   ════════════════════════════════════════════ */
export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNav = (href) => { setMenuOpen(false); navigate(href); };
  const isActive = (hrefs) => hrefs.some(h => location.pathname === h || location.pathname.startsWith(h + '/'));

  const navItems = [
    // ===== 核心产品入口 =====
    { label: 'AI旅行规划', href: '/planner' },
    { label: '探索目的地', href: '/explore' },
    { label: '旅行灵感', href: '/inspiration' },
    { label: '我的旅行', href: '/my-travel' },
    // ===== 功能菜单 =====
    { label: t('nav.aiGuide'), href: '/travel-ai' },
    { label: t('nav.planner'), type: 'dropdown', items: [
      { label: 'AI 行程历史', href: '/planner/history' },
      { label: 'AI 工作台', href: '/trip-studio' },
      { label: '酒店决策', href: '/hotel-intelligence' },
      { label: '决策追踪', href: '/decision-trace' },
    ]},
    { label: t('nav.community'), href: '/community' },
    { label: t('nav.diaries'), href: '/diaries' },
    { label: '创作', type: 'dropdown', items: [
      { label: '写日记', href: '/diary/new' },
      { label: '写游记', href: '/travelogues/new' },
      { label: '拍照姿势', href: '/poses' },
    ]},
    { label: t('nav.discover').split(' / ')[0], type: 'dropdown', items: [
      { label: '发现', href: '/discover' },
      { label: '景点', href: '/pois' },
      { label: '目的地', href: '/destinations' },
    ]},
    { label: t('nav.pointsCenter'), type: 'dropdown', items: [
      { label: '积分中心', href: '/points' },
      { label: '数据中心', href: '/dashboard' },
      { label: '旅行待办', href: '/checklist' },
      { label: '旅行记账', href: '/expense' },
    ]},
    { label: t('nav.scenes'), href: '/scenes' },
    { label: t('nav.profile').split(' / ')[0], type: 'dropdown', items: [
      { label: '个人中心', href: '/profile' },
      { label: '我的收藏', href: '/my-collections' },
      { label: '通知', href: '/notifications' },
      { label: '设置', href: '/settings' },
    ]},
  ];

  return (
    <>
      <style>{`
        /* ── 导航链接（Apple 式克制） ── */
        .nav-link {
          font-size: 13px;
          font-weight: 500;
          letter-spacing: -0.01em;
          color: rgba(200, 220, 240, 0.6);
          padding: 6px 10px;
          border-radius: 6px;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: color 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 3px;
          line-height: 1.2;
        }
        .nav-link:hover {
          color: rgba(245, 250, 255, 0.95);
          background: rgba(100, 180, 230, 0.06);
        }
        .nav-link-active {
          color: rgba(245, 250, 255, 1);
          background: rgba(100, 180, 230, 0.1);
        }

        /* ── 下拉面板 ── */
        .dropdown-positioner {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: 8px;
          z-index: 9999;
        }
        .dropdown-glass {
          min-width: 180px;
          padding: 6px;
          border-radius: 14px;
          background: rgba(8, 18, 36, 0.94);
          backdrop-filter: blur(32px) saturate(200%);
          -webkit-backdrop-filter: blur(32px) saturate(200%);
          border: 1px solid rgba(100, 160, 200, 0.1);
          box-shadow: 0 20px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(100, 160, 200, 0.05);
        }
        .dropdown-item {
          display: block;
          width: 100%;
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: -0.01em;
          color: rgba(200, 220, 240, 0.7);
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .dropdown-item:hover {
          background: rgba(80, 160, 220, 0.1);
          color: rgba(245, 250, 255, 0.95);
        }
        .dropdown-item-active {
          background: rgba(80, 160, 220, 0.12);
          color: rgba(245, 250, 255, 1);
        }

        /* ── 用户菜单 ── */
        .user-menu-glass {
          position: absolute;
          right: 0;
          top: calc(100% + 8px);
          min-width: 200px;
          padding: 6px;
          border-radius: 14px;
          background: rgba(8, 18, 36, 0.94);
          backdrop-filter: blur(32px) saturate(200%);
          -webkit-backdrop-filter: blur(32px) saturate(200%);
          border: 1px solid rgba(100, 160, 200, 0.1);
          box-shadow: 0 20px 48px rgba(0, 0, 0, 0.5);
          z-index: 9999;
        }
        .user-menu-item {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          color: rgba(200, 220, 240, 0.7);
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          transition: background 0.2s ease;
        }
        .user-menu-item:hover {
          background: rgba(80, 160, 220, 0.1);
          color: rgba(245, 250, 255, 0.95);
        }

        /* ── 导航栏毛玻璃（克制低饱和） ── */
        .nav-glass {
          background: rgba(6, 14, 28, 0.6);
          backdrop-filter: blur(28px) saturate(180%);
          -webkit-backdrop-filter: blur(28px) saturate(180%);
          border-bottom: 1px solid rgba(100, 160, 200, 0.06);
        }
        .nav-glass-scrolled {
          background: rgba(6, 14, 28, 0.88);
          backdrop-filter: blur(36px) saturate(200%);
          -webkit-backdrop-filter: blur(36px) saturate(200%);
          border-bottom: 1px solid rgba(100, 160, 200, 0.1);
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
        }

        /* ── 移动端 ── */
        .mobile-group-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 12px 8px;
          font-size: 14px;
          font-weight: 500;
          color: rgba(200, 220, 240, 0.85);
          background: transparent;
          border: none;
          cursor: pointer;
        }
        .mobile-group-items {
          overflow: hidden;
          padding-left: 16px;
          border-left: 1px solid rgba(100, 160, 200, 0.08);
          margin-left: 8px;
        }
        .mobile-item {
          display: block;
          width: 100%;
          padding: 8px;
          font-size: 13px;
          color: rgba(180, 200, 220, 0.65);
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
        }
        .mobile-menu {
          background: rgba(6, 14, 28, 0.96);
          backdrop-filter: blur(32px) saturate(200%);
          -webkit-backdrop-filter: blur(32px) saturate(200%);
          border-top: 1px solid rgba(100, 160, 200, 0.06);
        }

        /* ── 登录按钮 ── */
        .login-btn {
          font-size: 13px;
          font-weight: 500;
          letter-spacing: -0.01em;
          padding: 8px 20px;
          border-radius: 20px;
          background: transparent;
          border: 1px solid rgba(143, 169, 192, 0.2);
          color: rgba(220, 235, 250, 0.8);
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          white-space: nowrap;
        }
        .login-btn:hover {
          border-color: rgba(143, 169, 192, 0.4);
          color: rgba(255, 255, 255, 0.95);
          background: rgba(143, 169, 192, 0.05);
        }
      `}</style>

      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'nav-glass-scrolled' : 'nav-glass'}`}>
        <div className="w-full max-w-[1400px] mx-auto px-8 flex items-center" style={{ height: '56px' }}>

          {/* ── Logo ── */}
          <button onClick={() => navigate('/')} className="flex items-center gap-2.5 no-underline flex-shrink-0">
            <CatLogo size="sm" showText={false} />
            <div className="text-left hidden sm:block">
              <div className="text-[14px] font-semibold leading-tight" style={{ color: '#eef3f7', letterSpacing: '-0.02em' }}>西蒙的猫</div>
              <div className="text-[9px] leading-tight tracking-[0.2em] uppercase" style={{ color: 'rgba(158,200,224,0.4)' }}>Travel OS</div>
            </div>
          </button>

          {/* ── Desktop Nav（居中）── */}
          <div className="hidden lg:flex items-center flex-1 justify-center px-8">
            {navItems.map((item) => (
              item.type === 'dropdown' ? (
                <NavDropdown key={item.label} group={item} checkActive={isActive} onNav={handleNav} />
              ) : (
                <button key={item.label} onClick={() => handleNav(item.href)}
                  className={`nav-link ${isActive([item.href]) ? 'nav-link-active' : ''}`}>
                  {item.label}
                </button>
              )
            ))}
          </div>

            {/* ── Right: Language + User / Login ── */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Language Switcher */}
            <div className="hidden lg:block">
              <LanguageSwitcher compact />
            </div>
            <div className="hidden lg:block">
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-medium overflow-hidden transition-all duration-200"
                    style={{ background: 'linear-gradient(135deg, rgba(80,160,220,0.2), rgba(100,180,230,0.3))', border: '1px solid rgba(100,180,230,0.15)' }}>
                    <User className="w-3.5 h-3.5" style={{ color: 'rgba(220,245,255,0.85)' }} />
                  </button>
                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.97 }}
                        transition={springBounce}
                        className="user-menu-glass">
                        <div className="px-3 py-2.5 mb-1">
                          <div className="text-[13px] font-medium truncate" style={{ color: 'rgba(245,250,255,0.95)' }}>{user.name || user.email?.split('@')[0] || t('nav.traveler')}</div>
                          <div className="text-[11px] truncate mt-0.5" style={{ color: 'rgba(158,200,224,0.4)' }}>{user.email}</div>
                        </div>
                        <div className="h-px mx-2" style={{ background: 'rgba(100,160,200,0.08)' }} />
                        <button onClick={() => { setUserMenuOpen(false); navigate('/profile'); }} className="user-menu-item">
                          <User className="w-4 h-4" /> {t('nav.profile')}
                        </button>
                        <button onClick={() => { setUserMenuOpen(false); navigate('/settings'); }} className="user-menu-item">
                          <Settings className="w-4 h-4" /> {t('nav.accountSettings')}
                        </button>
                        <div className="h-px mx-2 my-1" style={{ background: 'rgba(100,160,200,0.08)' }} />
                        <button onClick={async () => { setUserMenuOpen(false); await logout(); navigate('/login'); }} className="user-menu-item" style={{ color: 'rgba(255,140,140,0.7)' }}>
                          <LogOut className="w-4 h-4" /> {t('nav.logout')}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <button onClick={() => navigate('/login')} className="login-btn">
                  {t('nav.login')}
                </button>
              )}
            </div>

            {/* Mobile hamburger */}
            <button className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-200" style={{ background: 'rgba(100,160,200,0.06)' }} onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X className="w-4 h-4" style={{ color: 'rgba(200,220,240,0.7)' }} /> : <Menu className="w-4 h-4" style={{ color: 'rgba(200,220,240,0.7)' }} />}
            </button>
          </div>
        </div>

        {/* ── Mobile Menu ── */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={spring}
              className="lg:hidden mobile-menu overflow-hidden">
              <div className="px-6 py-4 space-y-1">
                {navItems.map((item) => (
                  item.type === 'dropdown' ? (
                    <MobileGroup key={item.label} group={item} onNav={handleNav} />
                  ) : (
                    <button key={item.label} onClick={() => handleNav(item.href)}
                      className={`w-full text-left py-3 px-3 rounded-lg text-[14px] ${isActive([item.href]) ? 'nav-link-active' : ''}`}
                      style={{ color: 'rgba(200,220,240,0.85)', background: isActive([item.href]) ? 'rgba(80,160,220,0.08)' : 'transparent', fontWeight: 500 }}>
                      {item.label}
                    </button>
                  )
                ))}
                <div className="h-px my-3" style={{ background: 'rgba(100,160,200,0.06)' }} />
                {user ? (
                  <>
                    <button onClick={() => { setMenuOpen(false); navigate('/profile'); }} className="w-full text-left text-[14px] py-3 px-3 rounded-lg" style={{ color: 'rgba(200,220,240,0.85)' }}>{t('nav.profile')}</button>
                    <button onClick={() => { setMenuOpen(false); navigate('/settings'); }} className="w-full text-left text-[14px] py-3 px-3 rounded-lg" style={{ color: 'rgba(200,220,240,0.85)' }}>{t('nav.accountSettings')}</button>
                    <button onClick={async () => { setMenuOpen(false); await logout(); navigate('/login'); }} className="w-full text-left text-[14px] py-3 px-3 rounded-lg" style={{ color: 'rgba(255,140,140,0.7)' }}>{t('nav.logout')}</button>
                  </>
                ) : (
                  <button onClick={() => { setMenuOpen(false); navigate('/login'); }} className="w-full text-left text-[14px] py-3 px-3 rounded-lg" style={{ color: 'rgba(200,220,240,0.85)' }}>{t('nav.loginRegister')}</button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};
